import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import IdBadge from "components/IdBadge";
import { useMemo, useState } from "react";
import { regionKey } from "utils/regionKey";
import AxisDetailsDrawer from "./AxisDetailsDrawer";
import CreateAxisDialog from "./CreateAxisDialog";
import { useLidarClearance } from "./LidarClearanceContext";
import RegionFilterSelect from "./RegionFilterSelect";
import SortableHeaderCell from "./SortableHeaderCell";
import TableFilterField from "./TableFilterField";
import { buildGradient } from "./colorScaleDisplay";
import type { LidarClearanceAxis } from "./schemas";
import { createSortComparators } from "./sortComparators";
import t from "./translations";
import { useSortAndFilter } from "./useSortAndFilter";

const COLUMN_COUNT = 4;

interface AxisRow {
  axis: LidarClearanceAxis;
  gradient: string | undefined;
}

type AxisSortKey = "id" | "name" | "range";

const SORT_COMPARATORS = createSortComparators<AxisRow, AxisSortKey>({
  id: (row) => row.axis.id,
  name: (row) => row.axis.name_en,
  range: (row) => [row.axis.min_value, row.axis.max_value],
});

function matchesFilter(row: AxisRow, filter: string): boolean {
  return (
    String(row.axis.id).includes(filter) ||
    `${row.axis.region}-${row.axis.id}`.toLowerCase().includes(filter) ||
    row.axis.name_en.toLowerCase().includes(filter)
  );
}

export default function AxesTab() {
  const { axes, colorScaleByKey, regionTags, loading } = useLidarClearance();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [regionFilter, setRegionFilter] = useState("");

  const selectedAxis = useMemo(
    () =>
      axes.find((axis) => regionKey(axis.region, axis.id) === selectedKey) ??
      null,
    [axes, selectedKey]
  );

  const regionFilteredAxes = useMemo(
    () =>
      regionFilter ? axes.filter((axis) => axis.region === regionFilter) : axes,
    [axes, regionFilter]
  );

  const rows = useMemo(
    () =>
      regionFilteredAxes.map((axis) => {
        const scale = colorScaleByKey.get(
          regionKey(axis.region, axis.color_scale)
        );
        return {
          axis,
          gradient: scale
            ? buildGradient(scale.colors, axis.min_value, axis.max_value)
            : undefined,
        };
      }),
    [regionFilteredAxes, colorScaleByKey]
  );

  const { filterText, setFilterText, sortKey, sortDirection, toggleSort, visibleRows } =
    useSortAndFilter<AxisRow, AxisSortKey>({
      rows,
      matchesFilter,
      comparators: SORT_COMPARATORS,
    });

  return (
    <Box data-testid="axes-tab">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <RegionFilterSelect
            regionTags={regionTags}
            value={regionFilter}
            onChange={setRegionFilter}
            testId="axes-tab.region-filter"
          />
          <TableFilterField
            value={filterText}
            onChange={setFilterText}
            placeholder={t.FilterByIdOrNamePlaceholder}
            testId="axes-tab.filter"
          />
        </Box>
        <Button
          variant="contained"
          onClick={() => setCreateOpen(true)}
          data-testid="axes-tab.new-axis"
        >
          {t.NewAxisButton}
        </Button>
      </Box>
      <TableContainer component={Paper} data-testid="axes-table">
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "fixed",
            "& .MuiTableCell-root": { px: 1 },
          }}
        >
          <TableHead>
            <TableRow>
              <SortableHeaderCell
                sortKeyName="id"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={toggleSort}
                align="center"
                sx={{ width: 90 }}
              >
                {t.IdColumn}
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKeyName="name"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={toggleSort}
              >
                {t.NameColumn}
              </SortableHeaderCell>
              <SortableHeaderCell
                sortKeyName="range"
                activeSortKey={sortKey}
                direction={sortDirection}
                onSort={toggleSort}
                sx={{ width: 100 }}
              >
                {t.RangeColumn}
              </SortableHeaderCell>
              <TableCell sx={{ width: 160 }}>{t.ColorScaleColumn}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {axes.length === 0 ? "—" : t.NoMatchingRows}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
            {visibleRows.map(({ axis, gradient }) => {
              const key = regionKey(axis.region, axis.id);

              return (
                <TableRow
                  key={key}
                  hover
                  onClick={() => setSelectedKey(key)}
                  sx={{ cursor: "pointer" }}
                  data-testid={`axes-tab.row-${axis.region}-${axis.id}`}
                >
                  <TableCell align="center">
                    <IdBadge prefix={axis.region.toUpperCase()} id={axis.id} />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {axis.name_en}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {axis.min_value}–{axis.max_value}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 8,
                        backgroundColor: "grey.300",
                        backgroundImage: gradient,
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <AxisDetailsDrawer
        axis={selectedAxis}
        onClose={() => setSelectedKey(null)}
      />
      <CreateAxisDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
}
